import React, { useState, useContext, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import { useNavigate } from "react-router-dom";
import { useGoogleMaps } from 'context/GoogleContext';
import { FormContext } from 'context/FormContext';
import IconArrow from 'assets/images/icon_arrow.svg';
import defaultValues from 'forms/defaultValues';

const AddressAutocomplete = () => {
  const navigate = useNavigate();
  const { google } = useGoogleMaps();
  const { formValues, setFormValues } = useContext(FormContext);
  const [error, setError] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [shouldNavigate, setShouldNavigate] = useState(false);

  // wait for value to be persisted in local storage
  // neccessary because our guardedLoader can't access our FormContext, only local storage
  useEffect(() => {
    let interval;
    if (shouldNavigate &&
      formValues.Address.Address &&
      formValues.Address.Zip &&
      formValues.Address.LatLng.lat &&
      formValues.Address.LatLng.lng) {
      interval = setInterval(() => {
        const persisted = localStorage.getItem('formValues');
        if (persisted && JSON.parse(persisted).Address?.Address) {
          clearInterval(interval);
          navigate('/RoofType');
        }
      }, 50);
    }

    return () => clearInterval(interval);
  }, [shouldNavigate, formValues.Address.Address]);

  const resetAddress = () => {
    setFormValues({
      ...formValues,
      'Address': {
        'Address': '',
        'Zip': '',
        'LatLng': {}
      }
    });
  };

  const addressChange = (e) => {
    setError('');
    const AutocompleteService = new google.maps.places.AutocompleteService();
    const newAddress = e.target.value;
    // console.log('newAddress', newAddress);
    // when adress changes reset so user has to select a address?
    setFormValues({
      ...formValues,
      Address: {
        // ...formValues.Address,
        Address: newAddress,
        Zip: '',
        LatLng: {}
      }
    });
    

    if (!newAddress) {
      resetAddress();
    }

    AutocompleteService.getPlacePredictions({
      input: newAddress,
      language: 'de',
      componentRestrictions: { country: ['de', 'at', 'ch'] }
    }, (predictions, status) => {
      setPredictions(predictions);
    });
  };

  const selectAddress = (e) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ placeId: e.target.dataset.placeId }, (results, status) => {
      if (results && results[0]) {
        const result = results[0];

        let hasZipCode = false;
        let zipCode = "";
        let hasStreetNumber = false;
        let addressRawParts = [];
        let street = "";
        let city = "";

        for (const component of result.address_components) {

          if (component.types.includes("postal_code")) {
            hasZipCode = true;
            zipCode = component.long_name;
            addressRawParts.push({ order: "3", name: component.long_name });
          }
          if (component.types.includes("street_number")) {
            hasStreetNumber = true;
            addressRawParts.push({ order: "2", name: component.long_name });
            street = component.long_name; // prepend
          }
          if (component.types.includes("route")) {
            addressRawParts.push({ order: "1", name: component.long_name });
            street = component.long_name + " " + street; // append
          }
          if (component.types.includes("locality")) {
            addressRawParts.push({ order: "4", name: component.long_name });
            city = component.long_name;
          }
          if (component.types.includes("country")) {
            addressRawParts.push({ order: "5", name: component.long_name });
          }
        }

        if (!hasZipCode || !hasStreetNumber) {
          setError('Adresse ungültig, bitte geben Sie eine Hausnummer ein.');
          setPredictions([]);
          // resetAddress();
          setFormValues({
            ...formValues,
            Address: {
              Address: e.target.innerText, 
              Zip: '',
              LatLng: {}         
            }          
          });
          return;
        }

        const isSameAddress = (formValues.Address.Zip === zipCode && formValues.Address.Street === street && formValues.Address.City === city);

        setPredictions([]);
        setFormValues({
          ...formValues,
          Address: {
            Address: e.target.innerText,
            AddressRaw: addressRawParts.sort((a, b) => a.order - b.order).map(a => a.name).join(', '),
            Zip: zipCode,
            Street: street,
            City: city,
            LatLng: {
              lat: result.geometry.location.lat(),
              lng: result.geometry.location.lng()
            }
          },
          Roof: isSameAddress ? formValues.Roof : defaultValues.Roof
        });
        setShouldNavigate(true);
      }
    });
  };

  const checkAddressNextSlide = () => {
    
    if (formValues.Address.Address && !formValues.Address.Zip && !formValues.Address.LatLng.lat && !formValues.Address.LatLng.lng) {      
      // Recheck address
      const AutocompleteService = new google.maps.places.AutocompleteService();
      AutocompleteService.getPlacePredictions({
        input: formValues.Address.Address,
        language: 'de',
        componentRestrictions: { country: ['de', 'at', 'ch'] }
      }, (predictions, status) => {
        if (predictions.length == 0) {
          return;
        }
        const prediction = predictions[0];
        selectAddress({          
            target: {
              innerText: formValues.Address.Address,
              dataset: {
                placeId: prediction.place_id
              }
            }          
        });        
      });
    } else {
      if (formValues.Address.Address && formValues.Address.Zip && formValues.Address.LatLng.lat && formValues.Address.LatLng.lng) {
        navigate('/RoofType');
      } else {       
        setError('Bitte geben Sie eine gültige Adresse ein.');
      }
    }
  };

  return (
    <div className="AddressAutocomplete">
      <Form.Label htmlFor="Address">Ihre Adresse</Form.Label>
      <div className="ControlGroup">
        <Form.Control
          type="text"
          id="Address"
          aria-describedby="Address"
          onChange={addressChange}
          name="address2"
          value={formValues.Address.Address}
          autoComplete="off"
          role="presentation" />
        <img src={IconArrow} alt="Adresse suchen" onClick={checkAddressNextSlide} />
      </div>
      <div className="predictionResult">
        {predictions && predictions.map((item) => (
          <div
            key={item.place_id}
            onClick={selectAddress}
            data-place-id={item.place_id}
            title={item.description}>
            {item.description}
          </div>
        ))}
      </div>
      {error && <Alert key="danger" variant="danger">{error}</Alert>}
    </div>
  );
};

export default AddressAutocomplete;
